// src/components/Inv-Tracker/InvTracker.jsx
// ============================================================================
// BLOCK ITR.PAGE.002 — Inventory Tracker page shell
// ----------------------------------------------------------------------------
// PURPOSE
// - Renders the Inventory Tracker screen layout.
// - Owns filter controls, action buttons, and tree placement.
// - Delegates API/state logic to the feature controller hook.
// ============================================================================

import React from 'react';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import InvTrackerTree from './components/InvTrackerTree.jsx';
import useInvTrackerController from './hooks/useInvTrackerController.js';

// ============================================================================
// BLOCK ITR.PAGE.002.01 — Page constants
// ----------------------------------------------------------------------------
// PURPOSE
// - Centralize labels and UI defaults.
// ============================================================================
const REVIEW_FILTER_OPTIONS = [
  { value: 'active', label: 'Active (open + follow up)' },
  { value: 'open', label: 'Open only' },
  { value: 'follow_up', label: 'Follow up only' },
  { value: 'cleared', label: 'Cleared only' },
  { value: 'all', label: 'All rows' },
];

export default function InvTracker() {
  const {
    loading,
    submitting,
    error,
    filters,
    items,
    totals,
    selectedKeys,
    selectedRowMap,
    setFilterValue,
    toggleSelectedKey,
    replaceSelectedKeys,
    clearSelection,
    applyReviewStatus,
    refresh,
  } = useInvTrackerController();

  const [reviewNote, setReviewNote] = React.useState('');

  const saveActionItem = React.useCallback(() => {
    if (!selectedKeys.size || submitting) return;
    applyReviewStatus('follow_up', reviewNote);
  }, [applyReviewStatus, reviewNote, selectedKeys, submitting]);

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="h5">Inventory Tracker</Typography>

          <Typography variant="body2" color="text.secondary">
            Review inventory movements since the selected physical count, clear confirmed rows, or
            flag rows for follow up while keeping the entered-by and cleared-by audit trail.
          </Typography>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Warehouse"
              value={filters.warehouseCode}
              onChange={(event) => setFilterValue('warehouseCode', event.target.value)}
              size="small"
            />

            <TextField
              label="From"
              type="date"
              value={filters.fromDate}
              onChange={(event) => setFilterValue('fromDate', event.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />

            <TextField
              label="To"
              type="date"
              value={filters.toDate}
              onChange={(event) => setFilterValue('toDate', event.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />

            <TextField
              label="Search"
              value={filters.search}
              onChange={(event) => setFilterValue('search', event.target.value)}
              size="small"
              placeholder="Item, bin, lot, comment, SO..."
              fullWidth
            />

            <TextField
              label="Entered By"
              value={filters.userCode}
              onChange={(event) => setFilterValue('userCode', event.target.value)}
              size="small"
            />

            <TextField
              select
              label="Review Filter"
              value={filters.reviewFilter}
              onChange={(event) => setFilterValue('reviewFilter', event.target.value)}
              size="small"
              sx={{ minWidth: 220 }}
            >
              {REVIEW_FILTER_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>

            <Button variant="contained" onClick={refresh} disabled={loading || submitting}>
              Refresh
            </Button>
          </Stack>

          <Divider />

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Stack spacing={0.5}>
                <Typography variant="subtitle2">Queue Summary</Typography>
                <Typography variant="body2">Visible rows: {totals.totalVisibleRows}</Typography>
                <Typography variant="body2">Source rows: {totals.totalSourceRows}</Typography>
                <Typography variant="body2">Open: {totals.statusCounts.open}</Typography>
                <Typography variant="body2">Follow Up: {totals.statusCounts.follow_up}</Typography>
                <Typography variant="body2">Cleared: {totals.statusCounts.cleared}</Typography>
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, flex: 2 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">Selected Rows</Typography>
                <Typography variant="body2">Selected source rows: {selectedKeys.size}</Typography>

                <TextField
                  label="Action Item"
                  value={reviewNote}
                  onChange={(event) => setReviewNote(event.target.value)}
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Optional note saved on the selected row(s)"
                />

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                  <Button
                    variant="contained"
                    color="success"
                    disabled={!selectedKeys.size || submitting}
                    onClick={() => applyReviewStatus('cleared', reviewNote)}
                  >
                    Mark Cleared
                  </Button>

                  <Button
                    variant="contained"
                    color="warning"
                    disabled={!selectedKeys.size || submitting}
                    onClick={saveActionItem}
                  >
                    Save Action Item
                  </Button>

                  <Button
                    variant="outlined"
                    disabled={!selectedKeys.size || submitting}
                    onClick={() => applyReviewStatus('open', reviewNote)}
                  >
                    Reopen
                  </Button>

                  <Button variant="text" disabled={!selectedKeys.size} onClick={clearSelection}>
                    Clear Selection
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error">{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 1, minHeight: 320 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <InvTrackerTree
            items={items}
            selectedRowMap={selectedRowMap}
            toggleSelectedKey={toggleSelectedKey}
            replaceSelectedKeys={replaceSelectedKeys}
          />
        )}
      </Paper>
    </Stack>
  );
}
